import React, { useState, useEffect } from 'react';
import { FaClipboardCheck, FaPlus, FaCheck, FaTimes, FaHistory } from 'react-icons/fa';
import { productsApi, stockApi } from '../services/api';
import './InventoryCounts.css';

const InventoryCounts = () => {
    // State for products and counts
    const [products, setProducts] = useState([]);
    const [counts, setCounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for current count session
    const [currentCount, setCurrentCount] = useState(null);
    const [countItems, setCountItems] = useState([]);
    
    // State for search
    const [searchTerm, setSearchTerm] = useState('');
    
    // State for showing count history
    const [showHistory, setShowHistory] = useState(false);

    // Fetch products and counts when component mounts
    useEffect(() => {
        fetchProducts();
        fetchCounts();
    }, []);

    // Fetch products from API
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await productsApi.getAll();
            setProducts(Array.isArray(response.data) ? response.data : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Failed to fetch products. Please try again later.');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch counts from API
    const fetchCounts = async () => {
        try {
            const response = await stockApi.getAll();
            // Filter for only count transactions
            const countTransactions = Array.isArray(response.data) 
                ? response.data.filter(t => t.action === 'Count')
                : [];
            setCounts(countTransactions);
        } catch (err) {
            console.error('Error fetching counts:', err);
            setCounts([]);
        }
    };

    // Filter products based on search term
    // const filteredProducts = products.filter(product => 
    //     product.name.toLowerCase().includes(searchTerm.toLowerCase())
    // );

    // Start a new inventory count
    const startNewCount = () => {
        const newCount = {
            id: Date.now(),
            date: new Date().toISOString(),
            status: 'in-progress',
            notes: '',
            completedDate: null
        };
        
        setCurrentCount(newCount);
        
        // Initialize count items with current product stock levels
        const initialItems = products.map(product => ({
            productId: product.id,
            productName: product.name,
            expectedQuantity: product.stock,
            actualQuantity: '',
            difference: 0,
            notes: ''
        }));
        
        setCountItems(initialItems);
    };

    // Update count item quantity
    const updateCountQuantity = (productId, actualQuantity) => {
        setCountItems(countItems.map(item => {
            if (item.productId === productId) {
                const parsedQuantity = actualQuantity === '' ? '' : parseInt(actualQuantity);
                const difference = parsedQuantity === '' ? 0 : parsedQuantity - item.expectedQuantity;
                
                return {
                    ...item,
                    actualQuantity: parsedQuantity,
                    difference
                };
            }
            return item;
        }));
    };

    // Update count item notes
    const updateCountNotes = (productId, notes) => {
        setCountItems(countItems.map(item => {
            if (item.productId === productId) {
                return { ...item, notes };
            }
            return item;
        }));
    };

    // Complete the inventory count
    const completeCount = async () => {
        try {
            // Check if all items have been counted
            const uncountedItems = countItems.filter(item => item.actualQuantity === '');
            
            if (uncountedItems.length > 0) {
                if (!window.confirm(`There are ${uncountedItems.length} uncounted items. Do you want to continue anyway?`)) {
                    return;
                }
            }
            
            // Record each count as a transaction
            for (const item of countItems) {
                if (item.actualQuantity !== '') {
                    await stockApi.create({
                        productId: item.productId,
                        productName: item.productName,
                        action: 'Count',
                        amount: item.actualQuantity,
                        expectedAmount: item.expectedQuantity,
                        notes: item.notes,
                        date: new Date().toISOString()
                    });
                }
            }
            
            // Refresh counts list
            await fetchCounts();
            
            // Reset current count
            setCurrentCount(null);
            setCountItems([]);
        } catch (err) {
            console.error('Error completing count:', err);
            setError('Failed to complete count. Please try again.');
        }
    };

    // Cancel the current count
    const cancelCount = () => {
        if (window.confirm('Are you sure you want to cancel this inventory count? All progress will be lost.')) {
            setCurrentCount(null);
            setCountItems([]);
        }
    };

    // Reconcile inventory (update product stock based on count)
    const reconcileInventory = async (countId) => {
        if (window.confirm('Are you sure you want to reconcile inventory? This will update your product stock levels.')) {
            try {
                const countToReconcile = counts.find(count => count.id === countId);
                
                if (countToReconcile) {
                    // Update product stock level
                    await productsApi.update(countToReconcile.productId, { 
                        stock: countToReconcile.amount 
                    });
                    
                    // Refresh products list
                    await fetchProducts();
                }
            } catch (err) {
                console.error('Error reconciling inventory:', err);
                setError('Failed to reconcile inventory. Please try again.');
            }
        }
    };

    // View count details
    const viewCountDetails = (countId) => {
        // In a real app, this would navigate to a detailed view
        // For now, we'll just toggle the history view
        setShowHistory(true);
    };

    if (loading) {
        return <div className="loading">Loading inventory counts...</div>;
    }

    return (
        <div className="inventory-counts-container">
            <h1><FaClipboardCheck className="icon-space" /> Inventory Counts</h1>
            
            {error && <div className="error-message">{error}</div>}
            
            {!currentCount && !showHistory && (
                <div className="counts-dashboard">
                    <div className="counts-header">
                        <button className="btn-new-count" onClick={startNewCount}>
                            <FaPlus /> Start New Count
                        </button>
                        <button 
                            className="btn-view-history" 
                            onClick={() => setShowHistory(true)}
                            disabled={counts.length === 0}
                        >
                            <FaHistory /> View Count History
                        </button>
                    </div>
                    
                    <div className="counts-summary">
                        <div className="count-stat">
                            <h3>Total Counts</h3>
                            <div className="stat-value">{counts.length}</div>
                        </div>
                        <div className="count-stat">
                            <h3>Completed</h3>
                            <div className="stat-value">
                                {counts.filter(count => count.status === 'completed' || count.status === 'reconciled').length}
                            </div>
                        </div>
                        <div className="count-stat">
                            <h3>Reconciled</h3>
                            <div className="stat-value">
                                {counts.filter(count => count.status === 'reconciled').length}
                            </div>
                        </div>
                    </div>
                    
                    {counts.length > 0 && (
                        <div className="recent-counts">
                            <h2>Recent Counts</h2>
                            <div className="counts-list">
                                {counts.slice().reverse().slice(0, 5).map(count => (
                                    <div key={count.id} className="count-item">
                                        <div className="count-info">
                                            <div className="count-date">
                                                {new Date(count.date).toLocaleString()}
                                            </div>
                                            <div className={`count-status status-${count.status}`}>
                                                {count.status === 'in-progress' && 'In Progress'}
                                                {count.status === 'completed' && 'Completed'}
                                                {count.status === 'reconciled' && 'Reconciled'}
                                            </div>
                                        </div>
                                        <div className="count-actions">
                                            <button 
                                                className="btn-view" 
                                                onClick={() => viewCountDetails(count.id)}
                                            >
                                                View Details
                                            </button>
                                            {count.status === 'completed' && (
                                                <button 
                                                    className="btn-reconcile" 
                                                    onClick={() => reconcileInventory(count.id)}
                                                >
                                                    Reconcile
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            {currentCount && (
                <div className="count-session">
                    <div className="count-session-header">
                        <h2>Inventory Count - {new Date(currentCount.date).toLocaleString()}</h2>
                        <div className="count-actions">
                            <button className="btn-complete" onClick={completeCount}>
                                <FaCheck /> Complete Count
                            </button>
                            <button className="btn-cancel" onClick={cancelCount}>
                                <FaTimes /> Cancel
                            </button>
                        </div>
                    </div>
                    
                    <div className="count-session-body">
                        <div className="product-search">
                            <input 
                                type="text"
                                className="form-control"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        
                        <div className="count-items-list">
                            {countItems.map(item => (
                                <div key={item.productId} className="count-item">
                                    <div className="item-info">
                                        <span className="item-name">{item.productName}</span>
                                        <div className="item-controls">
                                            <div className="quantity-control">
                                                <label>Expected: {item.expectedQuantity}</label>
                                                <input 
                                                    type="number"
                                                    min="0"
                                                    value={item.actualQuantity}
                                                    onChange={(e) => updateCountQuantity(item.productId, e.target.value)}
                                                    placeholder="Actual quantity"
                                                />
                                                {item.actualQuantity !== '' && (
                                                    <span className={`difference ${item.difference >= 0 ? 'positive' : 'negative'}`}>
                                                        {item.difference >= 0 ? '+' : ''}{item.difference}
                                                    </span>
                                                )}
                                            </div>
                                            <input 
                                                type="text"
                                                placeholder="Notes"
                                                value={item.notes}
                                                onChange={(e) => updateCountNotes(item.productId, e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            
            {showHistory && (
                <div className="count-history">
                    <div className="history-header">
                        <h2>Count History</h2>
                        <button 
                            className="btn-back"
                            onClick={() => setShowHistory(false)}
                        >
                            Back to Dashboard
                        </button>
                    </div>
                    
                    <div className="history-table-container">
                        <table className="history-table">
                            <thead>
                                <tr>
                                    <th>Date & Time</th>
                                    <th>Product</th>
                                    <th>Expected</th>
                                    <th>Actual</th>
                                    <th>Difference</th>
                                    <th>Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {counts.map(count => (
                                    <tr key={count.id}>
                                        <td>{new Date(count.date).toLocaleString()}</td>
                                        <td>{count.productName}</td>
                                        <td>{count.expectedAmount}</td>
                                        <td>{count.amount}</td>
                                        <td className={count.amount >= count.expectedAmount ? 'positive' : 'negative'}>
                                            {count.amount >= count.expectedAmount ? '+' : ''}
                                            {count.amount - count.expectedAmount}
                                        </td>
                                        <td>{count.notes}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryCounts; 